<template>
  <v-list-item
    :value="item"
    :class="[getItemClasses(item.baseUrl), { 'disabled-state': loaderStore.isLoading }]"
    :elevation="getItemElevation(item.baseUrl)"
    @click="!loaderStore.isLoading && allowSelection && handleClick(item.baseUrl)"
  >
    <v-row class="align-center">
      <v-col>
        <v-list-item-title>{{ item.baseUrl }}</v-list-item-title>
        <v-list-item-subtitle>
          Created {{ formatTimeAgo(item.createdOn) }}
        </v-list-item-subtitle>
        <v-list-item-subtitle v-if="item.updatedOn">
          Last Updated {{ formatTimeAgo(item.updatedOn) }}
        </v-list-item-subtitle>
      </v-col>
      <v-col class="d-flex justify-end align-center" cols="auto">
        <v-chip 
          v-if="item.provider" 
          :color="getProviderColor(item.provider)" 
          size="small"
        >
          {{ item.provider }}
        </v-chip>
        <v-divider
          v-if="allowDeletion && !isActive(item.baseUrl) && item.provider"
          vertical
          class="mx-2"
        ></v-divider>
        <v-icon
          v-if="allowDeletion && !isActive(item.baseUrl)"
          x-small
          :class="{ 'disabled-icon': loaderStore.isLoading }"
          @click.stop="!loaderStore.isLoading && removeUrl && removeUrl(item.baseUrl)"
        >
          mdi-delete-outline
        </v-icon>
      </v-col>
    </v-row>
  </v-list-item>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useActiveUrlStore } from '@/store/activeUrlStore';
  import { useLoaderStore } from '@/store/loaderStore';  // Add loader store import
  import { formatTimeAgo } from '@vueuse/core';
  import IUrlHistoryItem from '@/interfaces/IUrlHistoryItem';
  import Provider from '@/enums/Provider';
  import { GoogleAnalytics } from '@/services/analytics/googleAnalytics';

  defineProps<{
    item: IUrlHistoryItem;
    removeUrl?: (baseUrl: string) => void;
    allowSelection?: boolean;
    allowDeletion?: boolean;
  }>();

  const emit = defineEmits<{ (e: 'select-url', url: string): void }>();

  const activeUrlStore = useActiveUrlStore();
  const loaderStore = useLoaderStore();  // Add loader store
  const activeUrl = computed(() => activeUrlStore.activeUrl);

  const isActive = (baseUrl: string) => baseUrl === activeUrl.value;

  const getItemClasses = (baseUrl: string) => [
    'v-list-item',
    'rounded',
    { 'v-list-item--active': isActive(baseUrl) },
    { 'pt-2 pb-2': isActive(baseUrl) },
  ];

  const getItemElevation = (baseUrl: string) => (isActive(baseUrl) ? 4 : 0);

  // Add provider color mapping
  const getProviderColor = (provider: Provider) => {
    switch(provider) {
      case Provider.KBS: return 'green';
      case Provider.IO: return 'blue';
      case Provider.ERS: return 'black';
      case Provider.OTHER: return 'grey';
      default: return 'primary';
    }
  };

  const handleClick = (baseUrl: string) => {
    activeUrlStore.setActiveUrl(baseUrl);
    GoogleAnalytics.urlSearchHistorySelected();
    emit('select-url', baseUrl);
  };
</script>

<style scoped>
.disabled-state {
  opacity: 0.6;
  pointer-events: none;
  cursor: not-allowed;
}

.disabled-icon {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>